import clsx from 'clsx';
import PropTypes from 'prop-types';
import React from 'react';
import { Link } from 'react-router-dom';
import { Icon } from 'ui';

/**
 * TextLink — enlace de texto con icono opcional.
 *
 * - `to` (sin `disabled`) → `<Link>` de react-router.
 * - `onClick` → `<button type="button">` nativo, para que entre en el ciclo de Tab y se
 *   active con Enter/Espacio (WCAG 2.1.1 / 4.1.2). Con `disabled` se marca con
 *   `aria-disabled` y sin `onClick`, en vez del atributo nativo `disabled`: así sigue
 *   recibiendo foco y eventos de ratón, y el Tooltip con el motivo que algunos
 *   consumidores envuelven alrededor (MyClassroomsViewTopRight) se sigue abriendo.
 *   `ariaDescribedBy` se reenvía al `<button>` para que el motivo (p. ej. de un Tooltip que
 *   envuelve al átomo) describa al elemento que recibe el foco, no a su envoltorio.
 * - Solo `disabled` (sin `onClick`) → `<span>` con el aspecto deshabilitado.
 */
const TextLink = ({ ariaDescribedBy, disabled, icon, onClick, text, to }) => {
  const className = clsx('text-link-component', { 'text-link-component--disabled': disabled });

  function renderContent() {
    return (
      <>
        {icon && <Icon name={icon} size="xl" color={disabled ? 'default' : 'inherit'} />}
        {text}
      </>
    );
  }

  if (to && !disabled) {
    return (
      <Link className="text-link-component" onClick={onClick} to={to}>
        {renderContent()}
      </Link>
    );
  }

  if (onClick) {
    return (
      <button
        aria-describedby={ariaDescribedBy}
        aria-disabled={disabled || undefined}
        className={clsx(className, 'text-link-component--button')}
        onClick={disabled ? undefined : onClick}
        type="button"
      >
        {renderContent()}
      </button>
    );
  }

  if (disabled) {
    return <span className={className}>{renderContent()}</span>;
  }

  return null;
};

TextLink.propTypes = {
  ariaDescribedBy: PropTypes.string,
  disabled: PropTypes.bool,
  icon: PropTypes.string,
  onClick: PropTypes.func,
  text: PropTypes.string,
  to: PropTypes.string,
};

export default TextLink;
